import { PopupSuccess } from "@components/generals/popup";
import { SpinerPage } from "@components/loaders/spiner";
import {
  PermitRequest,
  permitRequestSchema,
} from "@components/resolvers/permit.resolvers";
import { yupResolver } from "@hookform/resolvers/yup";
import { PermitService } from "@services/permit.service";
import { useNotificationStore } from "@stores/notification.store";
import { useThemeStore } from "@stores/theme.store";
import { useMutation, useQuery } from "@tanstack/react-query";
import id from "date-fns/locale/id";
import moment from "moment";
import lodash from "lodash";
import { useEffect, useState } from "react";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useForm } from "react-hook-form";
import { FaChevronDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { SchoolService } from "@services/school.service";
registerLocale("id", id);
setDefaultLocale("id");

export const PermitFormPage = () => {
  const navigate = useNavigate();
  const { setFooter, setHeader, setAppBar } = useThemeStore((state) => state);
  const { pushNotification } = useNotificationStore((state) => state);

  const [popupSuccess, setPopupSuccess] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [listOpenDate, setListOpenDate] = useState<Date[]>([]);
  // const [timeMode, setTimeMode] = useState<string>("");

  const prefix = localStorage.getItem("school-prefix") ?? "";
  const { data: settings } = useQuery({
    queryKey: [PermitService.queries.GET_PERMIT_SETTING, prefix],
    queryFn: () => PermitService.setting({ prefix }),
  });

  const { data: dataSiswa } = useQuery(
    [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    SchoolService.studentBio
  );
  const nis = dataSiswa?.data?.nis ?? "";

  const { data: dataGender } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_GENDER, prefix, nis],
    queryFn: () => SchoolService.studentGender({ prefix, nis }),
  });

  const { register, handleSubmit, setValue } = useForm<PermitRequest>({
    resolver: yupResolver<PermitRequest>(permitRequestSchema),
  });

  const { mutate, isLoading } = useMutation(PermitService.request, {
    onSuccess: (response) => {
      if (response.success) {
        setPopupSuccess(true);
      } else {
        pushNotification({
          type: "error",
          message: response.message,
        });
      }
    },
    onError: (error: unknown) => {
      pushNotification({
        type: "error",
        message: error instanceof Error ? error.message : "Internal Error",
      });
    },
  });

  useEffect(() => {
    setFooter(false);
    setHeader({
      type: "small",
      title: "Request Izin",
      buttonBack: true,
      avatar: true,
    });
    setAppBar({ title: "Request Izin" });
  }, [setFooter, setHeader, setAppBar]);

  useEffect(() => {
    if (nis) setValue("nis", nis);
  }, [nis, setValue]);

  // useEffect(() => {
  //   const currentDate = moment().clone();
  //   const endDate = moment().add(3, "months").toDate();
  //   const dateList = [];

  //   while (currentDate.isSameOrBefore(endDate)) {
  //     switch (timeMode) {
  //       case "KUNJUNGAN (Senin - Sabtu : 16.00 - 17.00)":
  //         if (currentDate.day() === 0) dateList.push(currentDate.toDate());
  //         break;
  //       case "KUNJUNGAN (Ahad : 07.00 - 17.00)":
  //         if (currentDate.day() !== 0) dateList.push(currentDate.toDate());
  //         break;
  //       case "KELUAR (Sabtu : 16.00 - 17.00)":
  //         if (
  //           currentDate.day() !== 6 ||
  //           (Cookies.get("gender") === "Pria"
  //             ? currentDate.week() % 2 !== 1
  //             : currentDate.week() % 2 !== 0)
  //         )
  //           dateList.push(currentDate.toDate());
  //         break;
  //       case "KELUAR (Ahad : 07.00 - 17.00)":
  //         if (
  //           currentDate.day() !== 0 ||
  //           (Cookies.get("gender") === "Pria"
  //             ? currentDate.week() % 2 !== 1
  //             : currentDate.week() % 2 !== 0)
  //         )
  //           dateList.push(currentDate.toDate());
  //         break;
  //       case "MENGINAP (Ahad)":
  //         if (
  //           currentDate.day() !== 0 ||
  //           (Cookies.get("gender") === "Pria"
  //             ? currentDate.week() % 2 !== 1
  //             : currentDate.week() % 2 !== 0)
  //         )
  //           dateList.push(currentDate.toDate());
  //         break;
  //     }
  //     currentDate.add(1, "days");
  //   }
  //   setListOpenDate(dateList);
  // }, [timeMode]);

  const onChangeMode = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue("mode", e.target.value);
    // const mode = e.target.value;
    const time = e.target.options[e.target.selectedIndex]?.dataset?.time ?? "";
    setListOpenDate(
      (settings?.data ?? [])
        .filter((item) => {
          const gender = dataGender?.data?.gender;
          const genderMap =
            gender?.toLowerCase() === "pria"
              ? "PUTRA"
              : gender?.toLowerCase() === "wanita"
                ? "PUTRI"
                : "SEMUA";
          return item.gender === genderMap || item.gender === "SEMUA";
        })
        .filter((item) => item.name === time)
        .map((item) => moment(item.date).toDate())
    );
    setStartDate(null);
    setValue("datePermit", "");
    setValue("dateBack", "");
  };

  const onChangeDate = (date: Date | null) => {
    if (date !== null) {
      setValue("dateBack", moment(date).format("YYYY-MM-DD HH:mm:ss"));
      setValue("datePermit", moment(date).format("YYYY-MM-DD HH:mm:ss"));
      setStartDate(date);
    }
  };

  return (
    <>
      <SpinerPage show={isLoading} />
      <div className="card card-style">
        <div className="content mb-4 !tw-mt-6">
          <form
            autoComplete="off"
            onSubmit={handleSubmit((data) => mutate(data))}
          >
            <input type="hidden" {...register("nis", { value: nis })} />
            <input type="hidden" {...register("prefix", { value: prefix })} />
            <div className="input-style has-borders input-style-always-active no-icon mb-3">
              <label htmlFor="form5" className="color-highlight font-500">
                Pilih Izin
              </label>
              <select
                className="bg-theme"
                {...register("mode")}
                onChange={onChangeMode}
              >
                <option value="">Pilih Izin</option>
                {lodash
                  .uniqBy(
                    (settings?.data ?? []).filter((item) => {
                      const gender = dataGender?.data?.gender;
                      const genderMap =
                        gender?.toLowerCase() === "pria"
                          ? "PUTRA"
                          : gender?.toLowerCase() === "wanita"
                            ? "PUTRI"
                            : "SEMUA";
                      return item.gender === genderMap;
                    }),
                    "name"
                  )
                  .map((item, idx) => (
                    <option key={idx} value={item.type} data-time={item.name}>
                      {item.name}
                    </option>
                  ))}
              </select>
              {/* <input type="hidden" {...register("time")} /> */}
              <span>
                <i>
                  <FaChevronDown />
                </i>
              </span>
            </div>
            <div className="input-style has-borders hnoas-icon input-style-always-active validate-field mb-4 mt-3">
              <DatePicker
                selected={startDate}
                minDate={new Date()}
                maxDate={moment().add(3, "months").toDate()}
                onChange={onChangeDate}
                className="form-control dark:form-control"
                dateFormat="dd MMMM yyyy"
                includeDates={listOpenDate}
                calendarStartDay={0}
                onChangeRaw={(e) => e.preventDefault()}
              />
              <span>
                <i>
                  <FaChevronDown />
                </i>
              </span>
              <label
                htmlFor="permit-date"
                className="color-highlight font-400 font-13"
              >
                Tanggal Izin
              </label>
            </div>
            <div className="input-style has-borders hnoas-icon input-style-always-active validate-field mb-4">
              <input
                type="text"
                className="form-control bg-theme"
                placeholder=""
                {...register("pickup")}
              />
              <label
                htmlFor="form1"
                className="color-highlight font-400 font-13"
              >
                Nama Penjemput
              </label>
            </div>
            <div className="input-style has-borders hnoas-icon input-style-always-active validate-field mb-4">
              {/* <input
                type="text"
                className="form-control bg-theme"
                placeholder="Status Penjemput"
                {...register("pickupStatus")}
              /> */}
              <label
                htmlFor="form1"
                className="color-highlight font-400 font-13"
              >
                Status Penjemput
              </label>
              <select {...register("pickupStatus")} className="bg-theme">
                <option value="default">Pilih Status</option>
                <option value="Orang Tua Kandung">Orang Tua Kandung</option>
                <option value="Paman/Kakek/Bibi">Paman/Kakek/Bibi</option>
                <option value="Kakak Laki-Laki/Perempuan Kandung">
                  Kakak Laki-Laki/Perempuan Kandung
                </option>
              </select>
              <span>
                <i>
                  <FaChevronDown />
                </i>
              </span>
            </div>
            <div className="input-style has-borders hnoas-icon input-style-always-active validate-field mb-4">
              <textarea
                className="form-control bg-theme"
                placeholder="Catatan ..."
                {...register("notes")}
              />
              <label
                htmlFor="form1"
                className="color-highlight font-400 font-13"
              >
                Catatan
              </label>
            </div>
            <button
              type="submit"
              className="btn btn-sm btn-full rounded-xs text-uppercase font-900 shadow-s bg-blue-dark tw-grow mt-4 tw-w-full tw-h-11"
            >
              Request Izin
            </button>
          </form>
        </div>
      </div>
      <PopupSuccess
        show={popupSuccess}
        title="Berhasil!"
        message="Berhasil membuat permintaan izin"
        textButton="Kembali"
        onConfirm={() => navigate(-1)}
      />
    </>
  );
};
